import RunButton from '@ui/button/RunButton';
import React, { useEffect, useRef } from 'react';
import CalendarMonth from '@app/state/calendar/CalendarMonth';
import ProcessRequestExecutionState from '@app/model/process-request/ProcessRequestExecutionState';
import { isStateFinishing } from '@app/model/process-request/ProcessRequestState';
import { useQueryClient } from '@tanstack/react-query';
import ProcessRequestResult from '@app/model/process-request/ProcessRequestResult';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import ButtonLinkList from '@app/component/button-link-list/ButtonLinkList';
import {
	useLatestProcessRequestExecutionStateMonitor,
} from '@app/site/secured/rateshopper/rates/provider/useLatestProcessRequestExecutionStateMonitor';
import Process from '@app/model/process-request/Process';
import DataProvider from '@app/model/_common/DataProvider';
import DataPlatform from '@app/model/_common/DataPlatform';
import RatesType from '@app/model/market_state/RatesType';


export const RefreshMarketStateButton: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	calendarMonth: CalendarMonth,
	filter: MarketStateFilterInput,
	withOta?: boolean
}> = ({ companyHotel, calendarMonth, filter, withOta = false }) => {

	const toaster = useToaster();
	const tools = useMarketStateTools();
	const monitor = useLatestProcessRequestExecutionStateMonitor();
	const handledRequestId = useRef<number | null>(null);
	const restoredActiveRequestId = useRef<number | null>(null);
	const output = isMarketStateProcess(monitor.latestActiveExecutionState, companyHotel.id, withOta)
		? monitor.latestActiveExecutionState
		: undefined;
	const { isDisabled } = useCalculateOutput(output);


	const triggerUpdate = (numberOfMonths: number, withOta: boolean, withProof = false) => {
		tools.triggerMarketStateUpdate(numberOfMonths, withOta, filter, withProof)
			.then((state) => {
				handledRequestId.current = null;
				restoredActiveRequestId.current = null;
				monitor.setLatestActiveExecutionState(new ProcessRequestExecutionState(state));
			})
			.catch(() => {
				toaster.error('Dashboard', 'Calendar could not be refreshed at the moment. Please try again later!');
			});

	};
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!output) return;

		if (!output.isFinished) {
			if (restoredActiveRequestId.current !== output.requestId) {
				restoredActiveRequestId.current = output.requestId;
				tools.toggleMarketStateIsUpdating(true);
			}
			return;
		}

		if (handledRequestId.current === output.requestId) return;

		handledRequestId.current = output.requestId;

		if (output.result === ProcessRequestResult.SUCCESS)
			toaster.success('Dashboard', 'Calendar refreshed successfully!');
		if (output.result === ProcessRequestResult.FAILED)
			toaster.error('Dashboard', 'Calendar could not be refreshed at the moment. Please try again later!');

		queryClient.invalidateQueries({
			queryKey: MarketStateGraphQLQueryKeys.lastUpdateDate(
				companyHotel.id,
				calendarMonth.index.year,
				calendarMonth.index.month,
				filter),
		});
		queryClient.invalidateQueries({
			queryKey: MarketStateGraphQLQueryKeys.details(
				companyHotel.id,
				calendarMonth.index.year,
				calendarMonth.index.month,
				filter),
		});
		queryClient.refetchQueries({
			queryKey: ['parity'],
			type: 'active',
		});
		restoredActiveRequestId.current = null;
		tools.toggleMarketStateIsUpdating(false);
		monitor.setLatestActiveExecutionState(null);
	}, [calendarMonth.index.month, calendarMonth.index.year, companyHotel.id, filter, monitor, output, queryClient, toaster, tools]);

	const enableMultiMonths = companyHotel.settings.calendar.enableMultiMonthsCalendarUpdate;
	const enableRefreshWithProof = companyHotel.settings.calendar.enableRefreshWithProof && !withOta
		&& filter.provider === DataProvider.BOOKING
		&& filter.platform === DataPlatform.WEB
		&& filter.ratesType === RatesType.PUBLIC;

	return enableMultiMonths || enableRefreshWithProof
		? <div className={`inline-flex rounded-md outline-none divide-x ${isDisabled ? 'divide-gray-200' : 'divide-blue-700'}`}>
			<RefreshButton triggerUpdate={triggerUpdate} withOta={withOta} output={output} />
			<RefreshButtonLinkList
				triggerUpdate={triggerUpdate}
				withOta={withOta}
				output={output}
				enableMultiMonths={enableMultiMonths}
				enableRefreshWithProof={enableRefreshWithProof} />
		</div>
		: <RefreshButton triggerUpdate={triggerUpdate} withOta={withOta} output={output} isStandAlone />;
};

const isMarketStateProcess = (output: ProcessRequestExecutionState | null, companyHotelId: number, withOta: boolean): output is ProcessRequestExecutionState => {
	if (!output) return false;
	if (output.companyHotelId !== companyHotelId) return false;

	return withOta
		? output.process === Process.COMPANY_HOTEL_MARKET_STATE_OTA
		: output.process === Process.COMPANY_HOTEL_MARKET_STATE;
};

const RefreshButton: React.FunctionComponent<{
	triggerUpdate: (numberOfMonths: number, withOta: boolean, withProof?: boolean) => void,
	withOta: boolean,
	output?: ProcessRequestExecutionState,
	isStandAlone?: boolean
}> = ({ triggerUpdate, withOta, output, isStandAlone }) => {
	const monitor = useLatestProcessRequestExecutionStateMonitor();
	const { isUpdatingMarketState, isUpdatingMarketStateFinishing, isDisabled, outputStats } = useCalculateOutput(output);

	return <RunButton
		label="Refresh Results"
		onClick={() => triggerUpdate(1, withOta)}
		disabled={isDisabled}
		joined={isStandAlone !== true}
		phase={monitor.isInteractionLocked && isUpdatingMarketStateFinishing ? 'finishing'
			: monitor.isInteractionLocked && isUpdatingMarketState ? 'running' : 'idle'}
		progress={outputStats} />;
};

const RefreshButtonLinkList: React.FunctionComponent<{
	triggerUpdate: (numberOfMonths: number, withOta: boolean, withProof?: boolean) => void,
	withOta: boolean,
	output?: ProcessRequestExecutionState,
	enableMultiMonths: boolean,
	enableRefreshWithProof: boolean
}> = ({ triggerUpdate, withOta, output, enableMultiMonths, enableRefreshWithProof }) => {
	const { isDisabled } = useCalculateOutput(output);
	const items = [
		...(enableRefreshWithProof ? [{
			name: 'Refresh with proof',
			onClick: () => triggerUpdate(1, withOta, true),
		}] : []),
		...(enableMultiMonths ? [
			{
				name: '2 months',
				onClick: () => triggerUpdate(2, withOta),
			},
			{
				name: '4 months',
				onClick: () => triggerUpdate(4, withOta),
			},
			{
				name: '6 months',
				onClick: () => triggerUpdate(6, withOta),
			},
		] : []),
	];

	return <ButtonLinkList
		state="primary"
		hideArrow
		disabled={isDisabled}
		extraButtonClassName="rounded-l-none px-2 py-1.5"
		description="Choose how to refresh the calendar."
		optionsSize="normal"
		items={items}><ChevronDownIcon
			className={`h-5 w-5 ${isDisabled ? 'text-gray-400' : 'text-white'}`} /></ButtonLinkList>;
};

const useCalculateOutput = (output?: ProcessRequestExecutionState): {
	isUpdatingMarketState: boolean,
	isUpdatingMarketStateFinishing: boolean,
	isDisabled: boolean,
	outputStats: number
} => {
	const monitor = useLatestProcessRequestExecutionStateMonitor();
	const tools = useMarketStateTools();

	const outputStats = output?.progressPercentage ?? 0;
	const isUpdatingMarketStateFinishing = !!output && !output.isFinished && isStateFinishing(output.state) && outputStats >= 100;
	const isUpdatingMarketState = !!output && !output.isFinished && !isUpdatingMarketStateFinishing;
	const isDisabled = monitor.isInteractionLocked || tools.calendarMonth.isInPast() || isUpdatingMarketState;

	return { isUpdatingMarketState, isUpdatingMarketStateFinishing, isDisabled, outputStats };
};
